import React, { useContext } from 'react';
import PropTypes from 'prop-types';

// components
import { Overlay } from './Overlay';
import { Subtitle } from './Subtitle';
import { TileOptions, TOOLTIP, TOAST } from './TileOptions';
import { Title } from './Title';
import { FavoriteIcons } from './FavoriteIcons';
import { ServerVarsContext } from '../../../../sharedComponents/ServerVarsContext';

// helpers
import classnames from 'classnames';
import SanitizeHtml from '../../../../utils/SanitizeHtml';

// constants
import { TILE_TYPES } from '../constants';
const { CATEGORY, COLLECTION, CREATOR, INTERIORS, ITEM } = TILE_TYPES;

// styles
import styles from './styles/FeedTile.scss';

export const FeedTile = props => {
    const { discoveryFeedTile, sharedFavoritesProps, userId, viewer, trackTileClick } = props;
    const { isMobile } = useContext(ServerVarsContext);

    if (!discoveryFeedTile) {
        return null;
    }

    const {
        ctaText,
        ctaUrl,
        description,
        header,
        imageUrl,
        itemCreator,
        itemId,
        reasonMessage,
        subtitle,
        subtitleUrl,
        tags,
        title,
        titleUrl,
        tileType,
    } = discoveryFeedTile;
    const showTileOverlay = tileType === INTERIORS || tileType === ITEM;
    const showTitleInOverlay = title && tileType === ITEM;
    const showCta = !!ctaText && ctaUrl && !showTileOverlay;
    const imageContainerClasses = classnames(
        styles.imageContainer,
        {
            [INTERIORS]: styles.interiorsTile,
            [CREATOR]: styles.creatorTile,
            [COLLECTION]: styles.collectionTile,
            [CATEGORY]: styles.categoryTile,
            [ITEM]: styles.itemTile,
        }[tileType]
    );
    const imageClasses = classnames(
        styles.image,
        {
            [CREATOR]: styles.creatorTile,
            [INTERIORS]: styles.interiorsTile,
        }[tileType]
    );

    return (
        <div className={styles.tileContainer}>
            <div className={styles.tile}>
                {!!imageUrl && (
                    <div className={imageContainerClasses}>
                        {isMobile ? (
                            <React.Fragment>
                                <a
                                    href={ctaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={trackTileClick}
                                >
                                    <img className={imageClasses} src={imageUrl} />
                                </a>
                                <FavoriteIcons
                                    itemId={itemId}
                                    userId={userId}
                                    viewer={viewer}
                                    imageUrl={imageUrl}
                                    title={title}
                                    {...sharedFavoritesProps}
                                />
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <img className={imageClasses} src={imageUrl} />
                                {showTileOverlay && (
                                    <Overlay
                                        className={styles.overlay}
                                        ctaText={ctaText}
                                        ctaUrl={ctaUrl}
                                        imageUrl={imageUrl}
                                        itemCreator={itemCreator}
                                        itemId={itemId}
                                        tags={tags}
                                        title={title}
                                        userId={userId}
                                        viewer={viewer}
                                        trackTileClick={trackTileClick}
                                        sharedFavoritesProps={sharedFavoritesProps}
                                        showTitleInOverlay={showTitleInOverlay}
                                    />
                                )}
                            </React.Fragment>
                        )}
                    </div>
                )}
                {!!header && <div className={styles.header}>{header}</div>}
                {!showTitleInOverlay && (
                    <Title
                        header={header}
                        title={title}
                        titleUrl={titleUrl}
                        trackTileClick={trackTileClick}
                    />
                )}
                <Subtitle
                    subtitle={subtitle}
                    subtitleUrl={subtitleUrl}
                    trackTileClick={trackTileClick}
                />
                {!!description && (
                    <div className={styles.descriptionContainer}>
                        <SanitizeHtml
                            className={styles.description}
                            allowedTags={[]}
                            allowedAttributes={{}}
                            content={description}
                        />
                    </div>
                )}
                {showCta && (
                    <div className={styles.cta}>
                        <a
                            href={ctaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={trackTileClick}
                        >
                            {ctaText}
                        </a>
                    </div>
                )}
                <TileOptions
                    ctaUrl={ctaUrl}
                    reasonMessage={reasonMessage}
                    optionsType={isMobile ? TOAST : TOOLTIP}
                />
            </div>
        </div>
    );
};

FeedTile.propTypes = {
    discoveryFeedTile: PropTypes.shape({
        ctaText: PropTypes.string,
        ctaUrl: PropTypes.string,
        description: PropTypes.string,
        header: PropTypes.string,
        imageUrl: PropTypes.string,
        itemCreator: PropTypes.string,
        itemId: PropTypes.string,
        reasonMessage: PropTypes.string,
        subtitle: PropTypes.string,
        subtitleUrl: PropTypes.string,
        tags: PropTypes.array,
        title: PropTypes.string,
        titleUrl: PropTypes.string,
        tileType: PropTypes.string,
    }),
    sharedFavoritesProps: PropTypes.object.isRequired,
    userId: PropTypes.string,
    viewer: PropTypes.object,
    trackTileClick: PropTypes.func.isRequired,
};
